import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Upload } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";

const empty = {
  title: "", slug: "", excerpt: "", content: "",
  cover_image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  tags: [], published: true,
};

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((prev) => ({ ...prev, cover_image: `${API}${data.url}` }));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const load = () => api.get("/admin/blog").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    try {
      const payload = { ...editing };
      delete payload._new; delete payload.id; delete payload.created_at; delete payload.author;
      if (editing._new) await api.post("/admin/blog", payload);
      else await api.put(`/admin/blog/${editing.id}`, payload);
      load(); setEditing(null);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Yazıyı silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="section-eyebrow">Yönetim</span>
          <h1 className="font-display text-4xl mt-2">Blog Yazıları</h1>
        </div>
        <button onClick={() => { setEditing({ ...empty, _new: true }); setErr(""); }} className="btn-primary text-sm" data-testid="new-blog-btn">
          <Plus className="w-4 h-4" /> Yeni Yazı
        </button>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-white/60 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">Başlık</th>
              <th className="text-left p-4">Slug</th>
              <th className="text-left p-4">Yayın</th>
              <th className="text-right p-4">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-white/5">
                <td className="p-4"><div className="text-white font-medium">{p.title}</div><div className="text-xs text-white/40 mt-1 line-clamp-1">{p.excerpt}</div></td>
                <td className="p-4 text-white/60">{p.slug}</td>
                <td className="p-4">{p.published ? <span className="text-green-400 text-xs">Yayında</span> : <span className="text-white/40 text-xs">Taslak</span>}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => { setEditing({ ...p, _new: false }); setErr(""); }} className="text-white/60 hover:text-gold p-2"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p.id)} className="text-white/60 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-white/50">Henüz blog yazısı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start justify-center p-4 md:p-10 overflow-y-auto">
          <div className="w-full max-w-3xl bg-surface border border-gold-30 p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl">{editing._new ? "Yeni Yazı" : "Yazıyı Düzenle"}</h2>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-gold"><X className="w-6 h-6" /></button>
            </div>
            {err && <div className="bg-red-950/40 border border-red-800 text-red-200 p-3 text-sm mb-4">{err}</div>}
            <div className="space-y-5">
              <Field label="Başlık"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="admin-input" /></Field>
              <Field label="Slug"><input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="admin-input" /></Field>
              <Field label="Kapak Görseli">
                <div className="flex items-center gap-4">
                  {editing.cover_image && (
                    <img src={editing.cover_image} alt="" className="w-20 h-20 object-cover border border-white/10" />
                  )}
                  <label className="btn-outline text-sm cursor-pointer">
                    <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Bilgisayardan Yükle"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleCoverUpload} className="hidden" disabled={uploading} />
                  </label>
                </div>
                <input value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className="admin-input mt-2" placeholder="veya görsel URL'si yapıştırın" />
              </Field>
              <Field label="Etiketler (virgülle ayır)"><input value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="admin-input" /></Field>
              <Field label="Özet"><textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="admin-input" /></Field>
              <Field label="İçerik (paragrafları boş satırla ayırın — link için [Metin](https://adres.com) yazın veya adresi direkt yapıştırın)"><textarea rows={10} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="admin-input" /></Field>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Yayında
              </label>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline text-sm">İptal</button>
              <button onClick={save} className="btn-primary text-sm"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <style>{`.admin-input { width: 100%; background: #0F0F14; border: 1px solid rgba(255,255,255,0.08); padding: 0.6rem 0.9rem; color: white; font-family: 'Outfit', sans-serif; outline: none; font-size: 0.875rem; } .admin-input:focus { border-color: rgba(212,175,55,0.6); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-widest text-white/60 mb-2 inline-block">{label}</span>{children}</label>;
}
